import { HttpClient, HttpContext } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { timeout } from "rxjs/operators";
import { environment } from "src/environments/environment";
import { ApiResponse } from "src/app/core/models/api.model";
import { PreviewChangeWire } from "src/app/core/models/subscription-checkout.model";
import { SKIP_SESSION_LOGOUT_ON_401 } from "../interceptors/auth.interceptor";

/**
 * A preview that never answers must not leave the caller's "Calculating…"
 * state spinning forever (issue #601 minor finding, S-20 residual) — it
 * must degrade into the same failure/Retry state a real error does, within
 * a bounded wait. Comfortably above the slowest real Stripe round-trip QA
 * measured (0.5–1.5s) and the PR's own slow-preview e2e fixtures (4s).
 */
const PREVIEW_TIMEOUT_MS = 20_000;

/**
 * Thin REST client for flymsg-node's `POST /api/v3/subscriptions/preview-change`
 * (flymsg-node issue #17/#57) — the fix for flymsg-fe issue #599: the
 * payment page used to show a client-computed `list price - coupon` figure
 * while Stripe actually charges a prorated invoice, so a paying customer
 * changing plans saw one number ("Pay $12.49") and got charged another
 * ($2.51). Every checkout on `PaymentDetailsComponent` and `NewPlanComponent`
 * — freemium, trial and paid plan changes alike — now asks this endpoint for
 * the amount instead (issue #599 product decision Q2, "one path
 * everywhere" — extended by the PR #601 human decision to /new-plan's "Due
 * today" too).
 *
 * Built on plain `HttpClient`, mirroring `NotificationApiService` (issue
 * #416) rather than the app's `DataService`: `DataService.apiURL` is
 * hardwired to the Laravel `environment.API_URL`, and its error-handling
 * machinery (plan-limit upsell dialogs, the `DEACTIVATED`-account flow,
 * `updatePlanStore()` side effects) is flymsg-be-shaped and doesn't apply to
 * this flymsg-node-hosted surface. The global `AuthInterceptor` still
 * attaches the Bearer token for every `HttpClient` request regardless of
 * target host.
 *
 * Every request here carries {@link SKIP_SESSION_LOGOUT_ON_401} (PR #601
 * G-01): node validates the bearer independently of Laravel and can reject
 * a token Laravel still accepts, so a 401 from THIS call must degrade the
 * preview (the caller's `previewFailed` state), never trigger the
 * interceptor's refresh -> extension-fallback -> full-app-logout escalation
 * — mirrors `PersonaAiApiService`/`NotificationRealtimeTokenService`'s
 * identical pattern for the same node surface.
 *
 * `environment.NODE_API_URL` must be set for every environment that serves
 * this page (`scripts/set-env.ts`) — production already sets it for the
 * notification center (issue #416); this reuses the same var.
 */
@Injectable({ providedIn: "root" })
export class SubscriptionPreviewApiService {
  constructor(private readonly http: HttpClient) {}

  /**
   * "What will I pay today" for `planIdentifier` (optionally with a coupon
   * code — validated by the existing `v1/subscriptions/coupon` check on the
   * manual payment-page flow, or handed through unvalidated when there is no
   * separate validation step, e.g. a URL-supplied code on `/new-plan`; node
   * resolves/refuses the coupon itself either way), computed server-side via
   * Stripe's own invoice preview — the single source of truth for every
   * figure a checkout surface shows (Plan/Discount/Total/Due today and the
   * Pay button). Works uniformly for a brand-new subscribe AND a plan change
   * (Stripe computes real proration for the latter); never compute these
   * amounts client-side.
   */
  previewChange(planIdentifier: string, coupon?: string | null): Observable<ApiResponse<PreviewChangeWire>> {
    const data: { plan_identifier: string; coupon?: string } = { plan_identifier: planIdentifier };
    if (coupon) {
      data.coupon = coupon;
    }
    return this.http
      .post<ApiResponse<PreviewChangeWire>>(`${environment.NODE_API_URL}/api/v3/subscriptions/preview-change`, data, {
        context: new HttpContext().set(SKIP_SESSION_LOGOUT_ON_401, true),
      })
      .pipe(timeout(PREVIEW_TIMEOUT_MS));
  }
}
